import { useJobs } from '@commenda/api';
import { JobListItem } from '@commenda/api/job/types';
import { JobType } from '@prisma/client';

const CSV_JOB_POLLING_INTERVAL = 5000;

export interface IndirectTaxCSVJobWatcherState {
  jobs: JobListItem[];
  refreshJobs: () => void;
  isCSVProcessingInProgress: () => boolean;
  watchWithRefresh: (refreshFn: () => Promise<void> | void) => () => void;
}

export const useIndirectTaxCSVJobWatcher = ({
  corporationId,
}: {
  corporationId: number;
}): IndirectTaxCSVJobWatcherState => {
  const { data, mutate } = useJobs({
    corporationId,
    jobType: JobType.sales_tax_csv_processing,
    options: {
      refreshInterval: CSV_JOB_POLLING_INTERVAL,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    },
  });

  const jobs = data?.jobs ?? [];

  const isCSVProcessingInProgress = (): boolean => {
    return jobs.some((job) => !job.isJobCompleted);
  };

  const watchWithRefresh = (
    refreshFn: () => Promise<void> | void
  ): (() => void) => {
    const intervalId = setInterval(async () => {
      if (isCSVProcessingInProgress()) {
        await refreshFn();
      } else {
        clearInterval(intervalId);
      }
    }, CSV_JOB_POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  };

  return {
    jobs,
    refreshJobs: mutate,
    isCSVProcessingInProgress,
    watchWithRefresh,
  };
};
